import React from 'react';
import '../styles/MapModal.css';
import MapCoordinateCanvas from './MapCoordinateCanvas';

/**
 * Reusable component for selecting a location on a map by clicking.
 * Can be used in both game mode (for guessing) and admin mode (for setting answers).
 *
 * @param {Object} props
 * @param {string|{thumb,preview,full}} props.mapImage - URL or progressive
 *        image bundle (from ``multi_res_urls``) of the map image to display
 * @param {Object} props.pinPosition - Current pin position {x, y} in normalized coordinates (0-1)
 * @param {Function} props.onPinChange - Callback when pin position changes (x, y)
 * @param {Object} props.showAnswer - Optional answer position to show {x, y}
 * @param {boolean} props.showAnswerMarker - Whether to show the answer marker
 * @param {string} props.mode - 'guess' or 'admin' - affects styling and behavior
 * @param {Function} props.onSubmit - Callback when submit button is clicked
 * @param {Function} props.onCancel - Optional cancel callback
 * @param {boolean} props.disabled - Whether the selector is disabled
 */
const MapPinSelector = ({
  mapImage,
  pinPosition,
  onPinChange,
  showAnswer,
  showAnswerMarker = false,
  mode = 'guess',
  onSubmit,
  onCancel,
  disabled = false,
  enableZoomPan = true,
  showZoomControls = true
}) => {
  const handleSelect = (pos) => {
    if (disabled || !pos) return;
    onPinChange(pos.x, pos.y);
  };

  if (!mapImage) {
    return (
      <div className="map-selector-loading">
        <p>Loading map...</p>
      </div>
    );
  }

  return (
    <div className="map-pin-selector">
      <MapCoordinateCanvas
        imageUrl={mapImage}
        onSelect={handleSelect}
        guess={pinPosition}
        answer={showAnswerMarker && showAnswer && typeof showAnswer.x === 'number' && typeof showAnswer.y === 'number' ? showAnswer : null}
        showLine={false}
        className={disabled ? 'disabled' : ''}
        enableZoomPan={enableZoomPan}
        showZoomControls={showZoomControls}
        panPaddingFractionX={0.5}
        panPaddingFractionY={0.75}
        panPaddingScaleWithZoom={true}
      />

      {(onSubmit || onCancel) && (
        <div className="map-selector-actions">
          {onSubmit && (
            <button
              onClick={() => pinPosition && onSubmit(pinPosition.x, pinPosition.y)}
              disabled={!pinPosition || disabled}
              className="gg-btn-submit"
            >
              {mode === 'admin' ? 'Set Answer' : 'Submit Guess'}
            </button>
          )}
          {onCancel && (
            <button onClick={onCancel} className="btn-cancel">
              Cancel
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MapPinSelector;
